import React, { useLayoutEffect, useRef, useState } from "react";
import { useRouteMatch } from "react-router-dom";

import EmailIcon from "@mui/icons-material/Email";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Divider, Stack, Typography } from "@mui/material";
import { WorkspaceAllFilesIcon, WorkspaceRecentlyAddedIcon } from "assets/material-custom-icons";
import { ArrowTrigger } from "components/old-data-box-components/Arrow-trigger/ArrowTrigger";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { Routes } from "constants/routes";
import { useDealParamsHooks, useNavigate } from "hooks";

import { useDealWorkspace } from "./context/DealWorkspace-context";
import { CloudMirror } from "./Data-box-components/CloudMirror";
import { Workspace } from "./Data-box-components/Workspace";
import { AddNewMenu } from "./Menus/AddNewMenu";

const COLLAPSED_WIDTH = 300;
const EXPANDED_WIDTH = 600;

// "All Files" + "Recently Added" (margins + 36px + 36px + cloud) need for dynamic height calculation for the folders list
const BOTTOM_SECTION_HEIGHT = 125;

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
  hint?: {
    title: string;
    icon: React.ReactElement;
  };
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, isActive, onClick, hint }) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{
      height: "36px",
      padding: "0 5px",
      borderRadius: "20px",
      cursor: "pointer",
      flexShrink: 0,
      backgroundColor: isActive ? "#DBE8F5" : "transparent",
      "&:hover": { backgroundColor: "#ECF1F8" }
    }}
    gap="5px"
  >
    <Stack sx={{ padding: "16px" }} />
    <Stack
      direction="row"
      alignItems="center"
      gap="5px"
      onClick={onClick}
      sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
    >
      {icon}
      <Typography sx={{ color: "#20293C", fontWeight: 400, fontSize: "16px" }}>{label}</Typography>
      {hint && (
        <MaterialTooltip isShow title={hint.title} centerText>
          {hint.icon}
        </MaterialTooltip>
      )}
    </Stack>
  </Stack>
);

export const DataBox = () => {
  const navigate = useNavigate();
  const { dealId } = useDealParamsHooks();
  const { emailFolder, currentFolder, refetchFolderMetadata } = useDealWorkspace();
  const [isExpanded, setIsExpanded] = useState(false);
  const allFilesMatch = useRouteMatch({ path: Routes.dealWorkspaceAllFiles, exact: true });
  const recentlyAddedMatch = useRouteMatch({ path: Routes.dealWorkspaceRecentlyAdded, exact: true });
  const emailMatch = useRouteMatch({ path: Routes.dealEmails, exact: true });
  const contentRef = useRef<HTMLDivElement>(null);
  const [availableHeightForFolders, setAvailableHeightForFolders] = useState(0);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const update = () => setAvailableHeightForFolders(Math.max(0, el.clientHeight - BOTTOM_SECTION_HEIGHT));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Stack sx={{ position: "relative", flexShrink: 0, width: `${COLLAPSED_WIDTH}px` }}>
      <Stack
        sx={{
          minWidth: `${COLLAPSED_WIDTH}px`,
          width: isExpanded ? `${EXPANDED_WIDTH}px` : `${COLLAPSED_WIDTH}px`,
          height: "100%",
          backgroundColor: "#f6f8fc",
          overflow: "hidden",
          transition: "width 0.3s ease-in-out, box-shadow 0.3s ease-in-out, left 0.3s ease-in-out",
          position: "absolute",
          left: 0,
          top: 0,
          zIndex: 10,
          ...(isExpanded && {
            boxShadow: "4px 0 16px rgba(0,0,0,0.15)"
          })
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ flexShrink: 0, marginBottom: "5px" }}
        >
          <Stack direction="row" alignItems="center" gap="5px">
            <AddNewMenu currentFolder={currentFolder} refetchHandler={refetchFolderMetadata} />
          </Stack>
          <ArrowTrigger
            onClick={() => setIsExpanded((v) => !v)}
            isOpen={isExpanded}
            closeState="right"
            openState="left"
            sx={{ ml: 0.5 }}
          />
        </Stack>
        <Stack
          ref={contentRef}
          sx={{ overflow: "auto", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
        >
          <Stack sx={{ flexShrink: 0, minHeight: 0 }}>
            <Workspace availableHeight={availableHeightForFolders} />
          </Stack>
          <Divider
            sx={{
              margin: "5px 10px 5px 0",
              backgroundColor: "none",
              borderBlockColor: "#DBE8F5 !important",
              height: "1px",
              flexShrink: 0
            }}
          />
          <NavItem
            icon={<WorkspaceAllFilesIcon sx={{ fill: "#0A3142", fontSize: 20 }} />}
            label="All Items"
            isActive={!!allFilesMatch}
            onClick={() => navigate({ route: "dealWorkspaceAllFiles", params: { deal_id: dealId } })}
          />
          <NavItem
            icon={<WorkspaceRecentlyAddedIcon sx={{ fill: "#0A3142", fontSize: 20 }} />}
            label="Recently Added"
            isActive={!!recentlyAddedMatch}
            onClick={() => navigate({ route: "dealWorkspaceRecentlyAdded", params: { deal_id: dealId } })}
            hint={{
              title: "Shows files added within the last 3 months",
              icon: <InfoOutlinedIcon sx={{ fontSize: 15, color: "#0A3142", opacity: 0.7 }} />
            }}
          />
          <Stack sx={{ flexShrink: 0, minHeight: 0 }}>
            <CloudMirror availableHeight={availableHeightForFolders} />
          </Stack>
          {emailFolder && (
            <NavItem
              icon={<EmailIcon sx={{ fill: "#0A3142", fontSize: 20 }} />}
              label="Email"
              isActive={!!emailMatch}
              onClick={() =>
                navigate({
                  route: "dealEmails",
                  params: { deal_id: dealId, folder_id: emailFolder?.id },
                  search: { section: emailFolder?.id }
                })
              }
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
